import {
  Box,
  Grid,
  HStack,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useMemo} from 'react';

import {splitAt} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../../../hooks/useCatalogue';
import i18n from '../../../../../i18n/i18n';
import {EquipmentScoreType} from '../../../../../types/MarketType';
import {getMostSearchedEquipmentItems} from '../utils/getMostSearchedEquipmentItems';

interface MostSearchedEquipmentProps extends TestIdProps {
  equipmentScore?: EquipmentScoreType;
}

function getLabelColor(isAvailable: boolean) {
  return match<boolean, ThemeColorTextPath>(isAvailable)
    .with(true, () => 'primary')
    .with(false, () => 'danger')
    .exhaustive();
}

function getRowBackgroundColor(index: number) {
  return match<number, ThemeColorPath>(index % 2)
    .with(0, () => 'palettes.neutral.10.100')
    .otherwise(() => 'general.transparent');
}

export function MostSearchedEquipment(props: MostSearchedEquipmentProps) {
  const {getFeatureLabel, hasFeatureTranslation} = useCatalogue();
  const mostSearchedEquipmentItems = useMemo(
    () =>
      getMostSearchedEquipmentItems(props.equipmentScore)
        .filter((item) => hasFeatureTranslation(item.featureCode))
        .map((item, index) => ({
          ...item,
          rank: index + 1,
          label: getFeatureLabel(item.featureCode),
        })),
    [props.equipmentScore, getFeatureLabel, hasFeatureTranslation]
  );

  const splitIndex = Math.ceil(mostSearchedEquipmentItems.length / 2);
  const columns = splitAt(splitIndex, mostSearchedEquipmentItems);

  return (
    <VStack spacing={6}>
      <HStack align="center" justify="center" spacing={2} wrap>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.overviewEquipment.labels.legend')}:
        </Text>
        <Text size="xSmall" alternative>
          {i18n.t('entity.overviewEquipment.labels.availableFeature')}
        </Text>
        <Text size="xSmall" color="danger" alternative>
          {i18n.t('entity.overviewEquipment.labels.missingFeature')}
        </Text>
      </HStack>
      <Text size="xSmall" color="secondary">
        {i18n.t('entity.overviewEquipment.labels.featuresOrderedByMostSearched')}
      </Text>
      <Grid columns={[1, 1, 2]} spacing={0}>
        {columns.map((column, columnIndex) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`column-${columnIndex}`}
            paddingHorizontal={4}
            borderColor="general.separator"
            borderRight={[
              undefined,
              undefined,
              columnIndex < columns.length - 1 ? '1px solid' : undefined,
            ]}
          >
            <VStack>
              {column.map((item, index) => (
                <Box
                  key={item.featureCode}
                  paddingHorizontal={2}
                  paddingVertical={1}
                  backgroundColor={getRowBackgroundColor(index)}
                  data-testid={suffixTestId('mostSearchedEquipmentRow', props)}
                >
                  <HStack>
                    <Box width={8}>
                      <Text size="xSmall" color="tertiary">
                        {`${item.rank}.`}
                      </Text>
                    </Box>
                    <Text size="xSmall" color={getLabelColor(item.isAvailable)} alternative>
                      {item.label}
                    </Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        ))}
      </Grid>
    </VStack>
  );
}
